import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Crosshair, MapPin, TrendingUp, Zap, Settings, Target, ArrowLeft, Coins, Home, BarChart3 } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Dashboard = () => {
  const { clientId } = useParams();
  const navigate = useNavigate();
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadDashboard();
  }, [clientId]);

  const loadDashboard = async () => {
    try {
      const res = await axios.get(`${API}/dashboard/${clientId}`);
      setDashboard(res.data);
    } catch (error) {
      console.error("Erro ao carregar dashboard:", error);
      toast.error("Erro ao carregar dados");
    } finally {
      setLoading(false);
    }
  };

  const createCampaign = async () => {
    if (!dashboard?.brands || dashboard.brands.length === 0) {
      toast.error("Nenhuma marca encontrada");
      return;
    }

    const brandId = dashboard.brands[0].id;
    
    try {
      toast.info("Criando campanha...");
      const res = await axios.post(`${API}/campaigns/create/${brandId}`);
      
      if (res.data.success) {
        toast.success("Campanha criada com sucesso!");
        loadDashboard();
      }
    } catch (error) {
      console.error("Erro ao criar campanha:", error);
      toast.error("Erro ao criar campanha");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600 mx-auto"></div>
          <p className="text-gray-400 mt-4">Carregando dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0a0a] relative overflow-hidden">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_left,_var(--tw-gradient-stops))] from-red-900/10 via-transparent to-transparent"></div>
      
      <div className="relative z-10 container mx-auto px-6 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate("/")}
              className="text-gray-400 hover:text-white"
            >
              <Home className="w-4 h-4 mr-1" />
              Início
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate("/login")}
              className="text-gray-400 hover:text-white"
              data-testid="btn-back-login"
            >
              <ArrowLeft className="w-4 h-4 mr-1" />
              Login
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate("/dev")}
              className="text-yellow-500 hover:text-yellow-400"
            >
              Painel DEV
            </Button>
          </div>
          
          <div className="flex items-center space-x-3">
            <Crosshair className="w-8 h-8 text-red-600" />
            <span className="text-2xl font-bold">DASHBOARD</span>
          </div>
          
          <div className="flex items-center space-x-3">
            <Button
              variant="outline"
              onClick={() => navigate(`/campaigns/${clientId}`)}
              className="border-red-600 text-red-600 hover:bg-red-600 hover:text-white"
              data-testid="btn-campaigns"
            >
              <Zap className="w-4 h-4 mr-2" />
              Campanhas
            </Button>
            <Button
              variant="outline"
              onClick={() => navigate(`/conversions/${clientId}`)}
              className="border-green-600 text-green-600 hover:bg-green-600 hover:text-white"
              data-testid="btn-conversions"
            >
              <BarChart3 className="w-4 h-4 mr-2" />
              Conversões
            </Button>
            <Button
              variant="outline"
              onClick={() => navigate(`/buy-tokens/${clientId}`)}
              className="border-yellow-600 text-yellow-600 hover:bg-yellow-600 hover:text-white"
              data-testid="btn-buy-tokens"
            >
              <Coins className="w-4 h-4 mr-2" />
              Tokens
            </Button>
            <Button
              variant="outline"
              onClick={() => navigate(`/settings/${clientId}`)}
              className="border-gray-700 hover:bg-gray-800"
              data-testid="btn-settings"
            >
              <Settings className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <Card className="bg-[#1a1a1a] border-gray-800" data-testid="stat-locations">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-gray-400">Locais Monitorados</CardTitle>
              <MapPin className="w-4 h-4 text-red-600" />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold">{dashboard?.stats?.total_locations || 0}</div>
            </CardContent>
          </Card>

          <Card className="bg-[#1a1a1a] border-gray-800" data-testid="stat-campaigns">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-gray-400">Campanhas Ativas</CardTitle>
              <Zap className="w-4 h-4 text-red-600" />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold">{dashboard?.stats?.active_campaigns || 0}</div>
            </CardContent>
          </Card>

          <Card className="bg-[#1a1a1a] border-gray-800" data-testid="stat-spend">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-gray-400">Gasto Total</CardTitle>
              <TrendingUp className="w-4 h-4 text-red-600" />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold">
                R$ {dashboard?.stats?.total_spend?.toFixed(2) || "0.00"}
              </div>
            </CardContent>
          </Card>

          <Card className="bg-[#1a1a1a] border-gray-800" data-testid="stat-conversions">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-gray-400">Conversões</CardTitle>
              <Target className="w-4 h-4 text-red-600" />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold">{dashboard?.stats?.total_conversions || 0}</div>
            </CardContent>
          </Card>
        </div>

        {/* Meta Connection Alert */}
        <div className="mb-6">
          <Card className="bg-gradient-to-r from-blue-900/20 to-blue-600/10 border-blue-600/30">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                📱 Conectar Meta (Facebook/Instagram)
              </CardTitle>
              <CardDescription className="text-gray-300">
                Conecte sua conta para criar campanhas reais no Facebook e Instagram
              </CardDescription>
            </CardHeader>
            <CardContent className="flex gap-3">
              <Button
                variant="outline"
                className="bg-blue-600 hover:bg-blue-700 text-white border-blue-500"
                onClick={() => navigate(`/connect-meta/${clientId}`)}
              >
                🔗 Conectar Agora
              </Button>
              <a
                href="https://developers.facebook.com/docs/marketing-api"
                target="_blank"
                rel="noopener noreferrer"
              >
                <Button
                  variant="ghost"
                  className="text-gray-300 hover:text-white"
                  type="button"
                >
                  Saiba Mais
                </Button>
              </a>
            </CardContent>
          </Card>
        </div>

        {/* Main Action */}
        <div className="mb-8">
          <Card className="bg-gradient-to-r from-red-900/20 to-red-600/10 border-red-600/30">
            <CardHeader>
              <CardTitle className="text-2xl">Criar Nova Campanha</CardTitle>
              <CardDescription className="text-gray-300">
                A IA irá criar e configurar sua campanha automaticamente
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Button
                size="lg"
                onClick={createCampaign}
                className="bg-red-600 hover:bg-red-700"
                data-testid="btn-create-campaign"
              >
                <Zap className="w-5 h-5 mr-2" />
                Gerar Campanha com IA
              </Button>
            </CardContent>
          </Card>
        </div>

        {/* Brands */}
        {dashboard?.brands && dashboard.brands.length > 0 && (
          <div className="mb-8">
            <h2 className="text-xl font-bold mb-4">Suas Marcas</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {dashboard.brands.map((brand) => (
                <Card
                  key={brand.id}
                  className="bg-[#1a1a1a] border-gray-800 hover:border-red-600/50 transition-all cursor-pointer"
                  onClick={() => navigate(`/brand/${brand.id}`)}
                  data-testid={`brand-card-${brand.id}`}
                >
                  <CardHeader>
                    <CardTitle>{brand.product_service}</CardTitle>
                    <CardDescription>{brand.city}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2 text-sm text-gray-400">
                      <p><span className="font-semibold">Objetivo:</span> {brand.goal}</p>
                      <p><span className="font-semibold">Público:</span> {brand.target_audience?.substring(0, 80)}...</p>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}

        {/* Recent Campaigns */}
        {dashboard?.recent_campaigns && dashboard.recent_campaigns.length > 0 && (
          <div className="mb-8">
            <h2 className="text-xl font-bold mb-4">Campanhas Recentes</h2>
            <div className="space-y-4">
              {dashboard.recent_campaigns.map((campaign) => (
                <Card
                  key={campaign.id}
                  className="bg-[#1a1a1a] border-gray-800"
                  data-testid={`campaign-card-${campaign.id}`}
                >
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <div>
                        <CardTitle className="text-lg">
                          Campanha {campaign.platform.toUpperCase()}
                        </CardTitle>
                        <CardDescription>
                          Status: <span className={`font-semibold ${
                            campaign.status === 'active' ? 'text-green-500' :
                            campaign.status === 'paused' ? 'text-yellow-500' :
                            'text-gray-500'
                          }`}>{campaign.status}</span>
                        </CardDescription>
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        className="border-gray-700 hover:bg-gray-800"
                        data-testid={`btn-view-campaign-${campaign.id}`}
                        onClick={() => navigate(`/campaigns/${clientId}`)}
                      >
                        Ver Detalhes
                      </Button>
                    </div>
                  </CardHeader>
                </Card>
              ))}
            </div>
          </div>
        )}

        {/* Monitored Locations */}
        {dashboard?.locations && dashboard.locations.length > 0 && (
          <div className="mb-8">
            <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
              <MapPin className="w-5 h-5 text-red-600" />
              Locais Monitorados ({dashboard.locations.length})
            </h2>
            <Card className="bg-[#1a1a1a] border-gray-800">
              <CardContent className="p-4">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 max-h-[500px] overflow-y-auto">
                  {dashboard.locations.map((location, idx) => (
                    <div
                      key={location.id || idx}
                      className="bg-[#252525] rounded-lg p-4 border border-gray-700 hover:border-red-600/50 transition-all"
                      data-testid={`location-card-${idx}`}
                    >
                      <div className="flex items-start gap-3">
                        <div className="bg-red-600/20 p-2 rounded-lg">
                          <MapPin className="w-4 h-4 text-red-500" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-white truncate" title={location.address}>
                            {location.address?.split(',')[0] || 'Local'}
                          </p>
                          <p className="text-sm text-gray-400 truncate" title={location.address}>
                            {location.city}
                          </p>
                          {location.category && (
                            <span className="inline-block mt-2 px-2 py-1 bg-gray-700 rounded text-xs text-gray-300">
                              {location.category}
                            </span>
                          )}
                          {location.geofence_radius_meters && (
                            <p className="text-xs text-gray-500 mt-1">
                              📍 Raio: {location.geofence_radius_meters}m
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Empty State */}
        {(!dashboard?.brands || dashboard.brands.length === 0) && (
          <Card className="bg-[#1a1a1a] border-gray-800" data-testid="empty-state">
            <CardHeader>
              <CardTitle>Nenhuma marca cadastrada</CardTitle>
              <CardDescription>
                Começe cadastrando sua primeira marca para criar campanhas
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Button
                onClick={() => navigate("/onboarding")}
                className="bg-red-600 hover:bg-red-700"
                data-testid="btn-start-onboarding"
              >
                Começar Agora
              </Button>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default Dashboard;
